import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import {
  apiDeactivateSchoolUser,
  apiReactivateSchoolUser,
  apiResendSencoSetupEmail,
} from '../../utils/rmsApi';

/**
 * RmsSchoolUserManageDialog (TDI-772, R-042)
 * ------------------------------------------
 * "Manage" on a SENCO's row of the School users page, Org Admins only
 * (Support Users see the list with no Manage; the callables enforce the same
 * gate). TDI-621's teacher-row pattern on the SENCO rows:
 *
 *   Invited:              "Send the set-up email again", "Deactivate"
 *   Active:               "Deactivate"
 *   Deactivated:          "Bring back"
 *
 * Each action confirms first with the agreed wording, then calls its
 * callable; the page's live subscription shows the new status. Pupils stay;
 * nothing is deleted. "Change details" and moving a leaver's pupils are
 * after go-live (TDI-264).
 */
export default function RmsSchoolUserManageDialog({ orgId, user, isOpen, onClose }) {
  // null = the action list; otherwise the action being confirmed.
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  if (!user) return null;
  const firstName = user.firstName || 'This person';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  const status = (user.status || '').toLowerCase();
  const school = user.schoolName || 'their school';

  const ACTIONS = {
    resend: {
      label: 'Send the set-up email again',
      title: 'Send the set-up email again?',
      body: [
        `We'll email ${firstName} a new link to set up their password, at ${user.email}. Any earlier link stops working.`,
      ],
      confirmLabel: 'Send email',
      run: () => apiResendSencoSetupEmail({ orgId, schoolUserId: user.schoolUserId }),
      done: `Set-up email sent to ${user.email}.`,
    },
    deactivate: {
      label: 'Deactivate',
      title: `Deactivate ${fullName}'s account?`,
      body: [
        `${firstName} won't be able to sign in. Their pupils stay on the platform and nothing is deleted. You can bring the account back later.`,
        `If ${school} has a new SENCO, register them first, then contact pilot support to move the pupils across.`,
      ],
      confirmLabel: 'Deactivate',
      run: () => apiDeactivateSchoolUser({ orgId, schoolUserId: user.schoolUserId }),
      done: `${fullName}'s account is deactivated.`,
    },
    bringBack: {
      label: 'Bring back',
      title: `Bring back ${firstName}'s account?`,
      body: [
        `${firstName} can sign in again with their password.`,
      ],
      confirmLabel: 'Bring back',
      run: () => apiReactivateSchoolUser({ orgId, schoolUserId: user.schoolUserId }),
      done: `${fullName}'s account is back.`,
    },
  };

  const available = status === 'deactivated'
    ? ['bringBack']
    : status === 'invited'
      ? ['resend', 'deactivate']
      : ['deactivate'];

  const close = () => {
    setConfirming(null);
    setError('');
    setNotice('');
    onClose();
  };

  const runConfirmed = async () => {
    const action = ACTIONS[confirming];
    if (!action || busy) return;
    setBusy(true);
    setError('');
    try {
      await action.run();
      setNotice(action.done);
      setConfirming(null);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const action = confirming ? ACTIONS[confirming] : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) close(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,480px)] bg-white rounded-xl shadow-xl z-50 p-5 text-left">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {action ? action.title : fullName}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close" className="p-1 rounded hover:bg-gray-100">
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </Dialog.Close>
          </div>

          {!action && (
            <div className="mt-1">
              <p className="text-sm text-gray-600">{user.email}</p>
              {notice && (
                <p role="status" className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                  {notice}
                </p>
              )}
              {error && (
                <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </p>
              )}
              <div className="mt-4 flex flex-col gap-2">
                {available.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setError(''); setNotice(''); setConfirming(key); }}
                    className="min-h-[44px] px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-900 hover:bg-gray-50 text-left"
                  >
                    {ACTIONS[key].label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {action && (
            <div className="mt-2">
              {action.body.map((line) => (
                <p key={line} className="text-sm text-gray-700 mt-2">{line}</p>
              ))}
              {error && (
                <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </p>
              )}
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirming(null)}
                  disabled={busy}
                  className="min-h-[44px] px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={runConfirmed}
                  disabled={busy}
                  className="min-h-[44px] px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
                >
                  {busy ? 'Working…' : action.confirmLabel}
                </button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
